import {ChangeDetectionStrategy, Component, Input} from '@angular/core';
import {BaseDialogDefinition, DelegateVerticalScrollingPosition} from '@aviary/dialogs/base-dialog/base-dialog.types';
import {FORM_DIALOG_DEFAULT_CANCEL_BUTTON_LABEL, FORM_DIALOG_DEFAULT_CONFIRM_BUTTON_LABEL, FormDialogDefinition} from './form-dialog.types';
import {omitPropertiesByKeyFrom} from '@aviary/store/transformation.helpers';
import {ExtendedFormGroup} from '@aviary/forms/extensions/extended-form-group/extended-form-group';
import {hasChanged, HasChangedFunction} from '@aviary/forms/form.helpers';
import {BaseDialogComponent} from '@aviary/dialogs/base-dialog/base-dialog.component';
import {SplitFormDialogSectionComponent} from '@aviary/dialogs/form-dialog/split-form-dialog-section/split-form-dialog-section.component';

@Component({
  selector: 'mx-form-dialog',
  host: {
    class: 'hk-delegate-vertical-scrolling grow'
  },
  templateUrl: './form-dialog.component.html',
  /*
    eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
    -- Need to be able to detect formGroup changes.  See https://stackoverflow.com/q/72690112.
  */
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [BaseDialogComponent, SplitFormDialogSectionComponent]
})
export class FormDialogComponent<ValueType> {

  @Input()
  public set definition(definition: FormDialogDefinition<ValueType>) {
    this.formGroup = definition.formGroup;
    this.allowConfirmWhenPristine = definition.allowConfirmWhenPristine ?? false;

    this.baseDialogDefinition = {
      cancelButtonLabel: FORM_DIALOG_DEFAULT_CANCEL_BUTTON_LABEL,
      confirmButtonLabel: FORM_DIALOG_DEFAULT_CONFIRM_BUTTON_LABEL,
      valueCalculator: () => this.formGroup.value,
      ...omitPropertiesByKeyFrom(definition, 'formGroup')
    };
  }

  @Input() public showCancelButton: boolean = true;
  @Input() public enableCancelButton: boolean = true;

  @Input() public showConfirmButton: boolean = true;
  @Input() public enableConfirmButton: boolean = true;

  @Input() public showAdditionalActionButtons: boolean = false;
  @Input() public additionalButtonsAreConfirm: boolean = false;

  // Used primarily by PersistentFormDialogComponent, and should not be used otherwise without good reason.
  @Input() public closeOnConfirm: boolean = true;

  /*
    These "confirmation pending" inputs are used primarily by PersistentFormDialogComponent, and should not be used otherwise without good reason.
    They change only the appearance and enablement of the confirmation button, not the flow of the dialog itself.
  */
  @Input() public confirmationPending: boolean = false;
  @Input() public showConfirmationPendingSpinner: boolean = true;

  @Input() public delegateVerticalScrolling: DelegateVerticalScrollingPosition = 'nowhere';

  public readonly hasChanged: HasChangedFunction = hasChanged;

  public baseDialogDefinition: BaseDialogDefinition<ValueType>;
  public formGroup: ExtendedFormGroup = new ExtendedFormGroup([]);
  public allowConfirmWhenPristine: boolean = false;
}
